import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { setup_product_tnc } from "services/apiSrv";
import { useParams } from "react-router-dom";
import { show_toast } from "utils/helpers";
import { api_srv } from "services";

// Validation schema
const validationSchema = Yup.object().shape({
  legal: Yup.object().shape({
    documentType: Yup.string().required("Document type is required"),
    legalSystemRef: Yup.string().required("Legal system reference is required"),
    uploadPath: Yup.string().required("Document upload is required"),
    document: Yup.mixed()
      .test("fileType", "Only PDF, DOC, or DOCX files are allowed", (value) =>
        value ? ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(value.type) : true
      )
      .test("fileSize", "File size must be less than 5MB", (value) =>
        value ? value.size <= 5 * 1024 * 1024 : true
      ),
  }),
});

export default function TncForm({ refetchProductDetails }) {
  const { id: productSystemRef } = useParams();
  const [document, setDocument] = useState(null);
  const [docUrl, setDocUrl] = useState("");

  // File upload function
  const uploadTncForm = async (options) => {
    const { onSuccess, onError, file } = options;
    try {
      const res = await (await api_srv).upload_api("tnc", file);
      if (!res.data || !res.data[0]?.fileUrl) {
        throw new Error("Invalid response from upload API");
      }
      setDocUrl(res.data[0].fileUrl);
      onSuccess(res.data[0].fileUrl);
      show_toast(`${file.name} uploaded successfully.`, "success");
    } catch (err) {
      console.error("File upload error:", err);
      onError(err);
      show_toast(`${file.name} upload failed: ${err.message}`, "error");
    }
  };

  const initialValues = {
    legal: {
      documentType: "DOCUMENT",
      legalSystemRef: "",
      uploadPath: "",
      document: null,
    },
  };

  const documentTypeOptions = [
    { value: "DOCUMENT", label: "Standard Document" },
    { value: "UPLOAD", label: "Upload" },
  ];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      productSystemRef,
      legal: {
        documentType: values.legal.documentType,
        legalSystemRef: values.legal.legalSystemRef,
        uploadPath: docUrl,
      },
    };
    try {
      await setup_product_tnc(payload);
      show_toast("Terms and Conditions saved successfully!", "success");
      refetchProductDetails?.();
      resetForm();
      setDocument(null);
      setDocUrl("");
    } catch (error) {
      console.error("API Error:", error);
      show_toast("Error saving terms and conditions: " + error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = (event, setFieldValue) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [".pdf", ".doc", ".docx"];
      const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!validTypes.includes(fileExtension)) {
        show_toast("Only PDF, DOC, or DOCX files are allowed.", "error");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        show_toast("File size must be less than 5MB.", "error");
        return;
      }
      setDocument({ name: file.name, originFileObj: file });
      setFieldValue("legal.document", file);

      uploadTncForm({
        file,
        onSuccess: (url) => {
          setFieldValue("legal.uploadPath", url);
          setDocument((prev) => ({ ...prev, url }));
        },
        onError: () => {
          setDocument(null);
          setDocUrl("");
          setFieldValue("legal.document", null);
          setFieldValue("legal.uploadPath", "");
        },
      });
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm my-4 tnc-form-container">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className="mb-3">
            <div className="card border tnc-form-card">
              <div className="card-header tnc-form-header">
                <h6 className="mb-0 fw-semibold">Terms and Conditions Configuration</h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {/* Document Type */}
                  <div className="col-md-6">
                    <label htmlFor="legal.documentType" className="form-label fw-semibold">
                      Document Type
                    </label>
                    <Field
                      as="select"
                      name="legal.documentType"
                      className="form-select form-control"
                      aria-describedby="documentTypeError"
                    >
                      {documentTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="legal.documentType"
                      component="div"
                      id="documentTypeError"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  {/* Legal System Reference */}
                  <div className="col-md-6">
                    <label htmlFor="legal.legalSystemRef" className="form-label fw-semibold">
                      Legal System Reference
                    </label>
                    <Field
                      name="legal.legalSystemRef"
                      placeholder="Enter legal system reference (e.g., 6789-xyz)"
                      className="form-control"
                      aria-describedby="legalSystemRefError"
                    />
                    <ErrorMessage
                      name="legal.legalSystemRef"
                      component="div"
                      id="legalSystemRefError"
                      className="invalid-feedback d-block"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="col-md-12">
                    <label htmlFor="legal.document" className="form-label fw-semibold">
                      Upload Document
                    </label>
                    <input
                      type="file"
                      accept=".pdf"
                      className="form-control"
                      onChange={(event) => handleFileUpload(event, setFieldValue)}
                      aria-describedby="documentError"
                    />
                    {document && (
                      <div className="mt-2">
                        Selected file: {document.name}
                        {document.url && (
                          <a href={document.url} target="_blank" rel="noopener noreferrer" className="ms-2">
                            View
                          </a>
                        )}
                      </div>
                    )}
                    <ErrorMessage
                      name="legal.document"
                      component="div"
                      id="documentError"
                      className="invalid-feedback d-block"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="d-flex justify-content-end mt-4">
              <button
                type="submit"
                className="btn btn-success btn-lg px-5 py-2 fw-semibold"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Terms and Conditions"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}